import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { formatDateTime } from "../../lib/format";
import { useTenantStore } from "../../stores/tenant";
import { useAuthStore } from "../../stores/auth";
import { Badge } from "../../components/ui/badge";
import type { Call } from "@shared/types";

export function CallsList() {
  const tenantId = useTenantStore((s) => s.currentTenantId);
  const isAdmin = useAuthStore((s) => s.user?.role === "admin");
  const [calls, setCalls] = useState<Call[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!tenantId) return;
    api<Call[]>(`/api/tenants/${tenantId}/calls`).then(setCalls).catch(() => setCalls([]));
  }, [tenantId]);

  return (
    <div>
      <header className="page-header">
        <div className="page-header__titles">
          <h1 className="page-header__title">Historique des appels</h1>
          <p className="page-header__subtitle">
            {isAdmin
              ? "Tous les appels reçus par vos agents, avec transcription et coût."
              : "Tous les appels reçus par votre assistant, avec transcription."}
          </p>
        </div>
      </header>
      <table className="table">
        <thead>
          <tr>
            <th>Date</th>
            <th>De</th>
            <th>Durée</th>
            <th>Intent</th>
            <th>Status</th>
            {isAdmin ? <th>Coût</th> : null}
            <th></th>
          </tr>
        </thead>
        <tbody>
          {calls.length === 0 ? (
            <tr>
              <td colSpan={isAdmin ? 7 : 6} className="table__empty">
                Aucun appel
              </td>
            </tr>
          ) : (
            calls.map((c) => (
              <tr
                key={c.id}
                className="table__row--link"
                onClick={() => navigate(`/calls/${c.id}`)}
              >
                <td>{formatDateTime(c.startedAt)}</td>
                <td style={{ fontFamily: "var(--mono)" }}>{c.fromNumber}</td>
                <td>{c.durationSec ? `${Math.round(c.durationSec / 60)} min` : "—"}</td>
                <td>{c.intent ?? "—"}</td>
                <td>
                  <StatusBadge status={c.status} />
                </td>
                {isAdmin ? <td>{c.costUsd ? `$${c.costUsd.toFixed(2)}` : "—"}</td> : null}
                <td className="table__actions">
                  <Link to={`/calls/${c.id}`}>Détail</Link>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function StatusBadge({ status }: { status: Call["status"] }) {
  const map: Record<Call["status"], { variant: Parameters<typeof Badge>[0]["variant"]; label: string }> = {
    "ringing": { variant: "warning", label: "En sonnerie" },
    "in-progress": { variant: "live", label: "En cours" },
    "completed": { variant: "success", label: "Terminé" },
    "failed": { variant: "danger", label: "Échec" },
    "transferred": { variant: "primary", label: "Transféré" },
  };
  const { variant, label } = map[status];
  return <Badge variant={variant}>{label}</Badge>;
}
